import {Injectable} from '@angular/core'

import {Observable} from 'rxjs'
import {map} from 'rxjs/operators'

import {ChartConfiguration, ChartDataSets} from 'chart.js'

import {LoggerService} from '@app/core/services/logger.service'
import {DataService} from '@app/core/services/data/data.service'
import {
  ElasticQuery,
  TermsAggregation,
  DateAggregation,
  MetricAggregation,
  RangeFilter,
  TermFilter,
  Data,
} from '@app/core/services/rest-api.model'
import {SearchFilter} from '../search-filter'
import {DateFilter} from '@app/core/services/date-filter/date-filter.interface'

export interface TransactionChartData {
  chartName: string
  chartConfig: ChartConfiguration
}

export interface TransactionTotal {
  name: string
  amount: number
  count: number
}

export interface TransactionData {
  totals: {[key: string]: TransactionTotal}
  charts: {[key: string]: TransactionChartData}
}

export enum XAxisType {
  HOUR,
  DAY,
  WEEK,
  FOUR_WEEKS,
  MONTH,
  TERMINAL,
  ITEM_REF,
}

export enum StackType {
  CHD,
  DISPOSITION,
  STATE,
  HOST,
  TYPE,
}

@Injectable()
export class TransactionChartDataService {
  constructor(
    private dataService: DataService,
    private logger: LoggerService,
  ) {}

  public getCurrencies(
    domain: string,
    filter: SearchFilter,
    range: DateFilter,
  ): Observable<string[]> {
    const elasticQuery = new ElasticQuery('Transactions', '', 0)

    elasticQuery.addAggregation(
      new TermsAggregation('currency', 'currency', 50),
    )

    elasticQuery.offset = 0
    if (range) {
      elasticQuery.addFilter(new RangeFilter(range.start, range.end))
    }

    elasticQuery.addFilter(new TermFilter('siteAccessDomain', domain))
    elasticQuery.addFilter(new TermFilter('complete', 'true'))

    if (filter) {
      elasticQuery.query = filter.query
    }

    /* Changes to inputs so reload chart data */
    return this.dataService.getData(elasticQuery).pipe(
      map((res) => {
        return res.aggregations['currency'].buckets.map((elem) => elem.name)
      }),
    )
  }

  public getTransactionChartData(
    domain: string,
    filter: SearchFilter,
    range: DateFilter,
    currency: string,
    xAxisType: XAxisType,
    stackType: StackType,
  ): Observable<TransactionData> {
    const elasticQuery = this.createElasticQuery(
      domain,
      filter,
      range,
      currency,
      xAxisType,
      stackType,
    )
    /* Changes to inputs so reload chart data */
    return this.dataService.getData(elasticQuery).pipe(
      map((res) => {
        /* Now build new chart configurations for the new data */
        return this.createChartDataFromElasticResult(res, xAxisType)
      }),
    )
  }

  private createElasticQuery(
    domain: string,
    filter: SearchFilter,
    range: DateFilter,
    currency: string,
    xAxisType: XAxisType,
    stackType: StackType,
  ): ElasticQuery {
    const elastic = new ElasticQuery('Transactions', '', 0)

    /* Configure XAxis aggregation */
    var xAxisAggr
    switch (xAxisType) {
      case XAxisType.HOUR:
        xAxisAggr = new DateAggregation('x', 'hour', 0)
        if (range) {
          xAxisAggr.minBound = range.start
          xAxisAggr.maxBound = range.end
        }
        xAxisAggr.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        xAxisAggr.timeFormat = 'HH:mm'
        break
      case XAxisType.DAY:
        xAxisAggr = new DateAggregation('x', 'day', 0)
        if (range) {
          xAxisAggr.minBound = range.start
          xAxisAggr.maxBound = range.end
        }
        xAxisAggr.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        xAxisAggr.timeFormat = 'dd-MMM-yyyy'
        break
      case XAxisType.WEEK:
        xAxisAggr = new DateAggregation('x', 'week', 0)
        if (range) {
          xAxisAggr.minBound = range.start
          xAxisAggr.maxBound = range.end
        }
        xAxisAggr.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        xAxisAggr.timeFormat = 'ww'
        break
      case XAxisType.FOUR_WEEKS:
        xAxisAggr = new DateAggregation('x', '4w', 0)
        if (range) {
          xAxisAggr.minBound = range.start
          xAxisAggr.maxBound = range.end
        }
        xAxisAggr.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        xAxisAggr.timeFormat = 'wwww'
        break
      case XAxisType.MONTH:
        xAxisAggr = new DateAggregation('x', 'month', 0)
        if (range) {
          xAxisAggr.minBound = range.start
          xAxisAggr.maxBound = range.end
        }
        xAxisAggr.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        xAxisAggr.timeFormat = 'MMM'
        break
      case XAxisType.TERMINAL:
        xAxisAggr = new TermsAggregation('x', 'serialNumber', 50)
        break
      case XAxisType.ITEM_REF:
        xAxisAggr = new TermsAggregation('x', 'itemRef', 50)
        break
    }

    /* Now the stack aggregation */
    var stackAggr
    switch (stackType) {
      case StackType.CHD:
        stackAggr = new TermsAggregation('stack', 'ifd', 50)
        break
      case StackType.DISPOSITION:
        stackAggr = new TermsAggregation('stack', 'disposition', 50)
        break
      case StackType.STATE:
        stackAggr = new TermsAggregation('stack', 'state', 50)
        break
      case StackType.HOST:
        stackAggr = new TermsAggregation('stack', 'host', 50)
        break
      case StackType.TYPE:
        stackAggr = new TermsAggregation('stack', 'type', 50)
        break
    }

    /* And the aggregation for the total comittedAmount */
    const sumAggr = new MetricAggregation('total', 'committedAmount', 'sum')
    const countAggr = new MetricAggregation('count', 'id', 'count')

    /* Now build up the aggregation tree */
    /* query -> XAxis -> Stack -> Total
     *   |   -> Stack -> Total
     *   |   -> Total
     */

    xAxisAggr.addAggregation(stackAggr)
    stackAggr.addAggregation(sumAggr)

    elastic.addAggregation(xAxisAggr)
    elastic.addAggregation(stackAggr)
    elastic.addAggregation(sumAggr)
    elastic.addAggregation(countAggr)

    elastic.offset = 0
    if (range) {
      elastic.addFilter(new RangeFilter(range.start, range.end))
    }

    elastic.addFilter(new TermFilter('siteAccessDomain', domain))
    elastic.addFilter(new TermFilter('complete', 'true'))
    elastic.addFilter(new TermFilter('currency', currency))

    if (filter) {
      elastic.query = filter.query
    }

    return elastic
  }

  private createChartDataFromElasticResult(
    data: Data,
    xAxisType: XAxisType,
  ): TransactionData {
    var txnCharts: TransactionData = {
      totals: {},
      charts: {},
    }

    /* Now the count chart */
    var stackNames = []

    /* First get list of all stack values in all returned results */
    data.aggregations['stack'].buckets.map((elem) => {
      stackNames.push(elem.name)
    })

    /* Revenue chart */
    txnCharts.charts['Revenue'] = {
      chartName: 'Revenue',
      chartConfig: this.createChartFromElasticResult(
        data.aggregations['x'],
        stackNames,
        true,
        xAxisType,
      ),
    }

    /* Count chart */
    txnCharts.charts['Count'] = {
      chartName: 'Count',
      chartConfig: this.createChartFromElasticResult(
        data.aggregations['x'],
        stackNames,
        false,
        xAxisType,
      ),
    }

    /* Grand Total */
    txnCharts.totals['Total'] = {
      name: 'Total',
      amount: data.aggregations['total'].value / 100,
      count: Math.floor(data.aggregations['count'].value),
    }

    /* Now totals */
    data.aggregations['stack'].buckets.map((elem) => {
      txnCharts.totals[elem.name] = {
        name: elem.name,
        amount: elem.aggregations['total'].value / 100,
        count: elem.count,
      }
    })

    return txnCharts
  }

  private createChartFromElasticResult(
    aggr: any,
    dataSetNames: string[],
    total: boolean,
    xAxisType: XAxisType,
  ): ChartConfiguration {
    var chartLabels = aggr.buckets.map((elem) => {
      if (xAxisType === XAxisType.DAY) {
        /* Render the day of week on a second line below the date.
         * Chart.js renders an array of strings as a multi-line label. */
        const dayOfWeek = this.dayOfWeekFromDateLabel(elem.name)
        return dayOfWeek ? [elem.name, dayOfWeek] : elem.name
      }
      return elem.name
    })
    var chartDatasetsMap: {[key: string]: ChartDataSets} = {}
    var chartDatasets: ChartDataSets[] = []
    var chartPalette = palette(dataSetNames.length).map((hex) => '#' + hex)

    /* Init the datasets map and array */
    dataSetNames.forEach((dsn) => {
      chartDatasetsMap[dsn] = {
        data: [],
        label: dsn,
        stack: 'stack',
        backgroundColor: this.hexToRgbA(
          chartPalette[dataSetNames.indexOf(dsn)],
          0.2,
        ),
        borderColor: this.hexToRgbA(chartPalette[dataSetNames.indexOf(dsn)], 1),
        borderWidth: 2,
      }

      chartDatasets.push(chartDatasetsMap[dsn])
    })

    aggr.buckets.map((elem) => {
      elem.aggregations['stack'].buckets.map((stack) => {
        if (chartDatasetsMap[stack.name]) {
          if (total) {
            ;(chartDatasetsMap[stack.name].data as number[]).push(
              stack.aggregations['total'].value / 100,
            )
          } else {
            ;(chartDatasetsMap[stack.name].data as number[]).push(stack.count)
          }
        } else {
          this.logger.error('missing data set in map')
        }
      })

      /* Now for each data set that was not listed add a 0 value to complete the series */
      Object.keys(chartDatasetsMap).forEach((key) => {
        if (
          !elem.aggregations['stack'].buckets.find((stack) => stack.name == key)
        ) {
          ;(chartDatasetsMap[key].data as number[]).push(0)
        }
      })
    })

    return {
      type: 'bar',
      data: {
        labels: chartLabels,
        datasets: chartDatasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
        },
        scales: {
          xAxes: [
            {
              // display: true
              // stacked: true
            },
          ],
          yAxes: [
            {
              // display: true,
              stacked: true,
            },
          ],
        },
      },
    }
  }

  private dayOfWeekFromDateLabel(label: string): string | null {
    /* Expects "dd-MMM-yyyy" (e.g. "13-May-2026") matching the DAY timeFormat. */
    const match = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/.exec(label)
    if (!match) {
      return null
    }
    const day = parseInt(match[1], 10)
    const monthIdx = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ].indexOf(match[2])
    const year = parseInt(match[3], 10)
    if (monthIdx < 0) {
      return null
    }
    const date = new Date(year, monthIdx, day)
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]
  }

  private hexToRgbA(hex: string, alpha: number): string {
    var c
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
      c = hex.substring(1).split('')
      if (c.length == 3) {
        c = [c[0], c[0], c[1], c[1], c[2], c[2]]
      }
      c = '0x' + c.join('')
      return (
        'rgba(' +
        [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') +
        ',' +
        alpha +
        ')'
      )
    }
    throw new Error('Bad Hex')
  }
}

function palette(count: number): string[] {
  if (count <= 0) return []
  if (count === 1) return ['7fc97f']

  const palettes: {[size: number]: string[]} = {
    3: ['7fc97f', 'beaed4', 'fdc086'],
    4: ['7fc97f', 'beaed4', 'fdc086', 'ffff99'],
    5: ['7fc97f', 'beaed4', 'fdc086', 'ffff99', '386cb0'],
    6: ['7fc97f', 'beaed4', 'fdc086', 'ffff99', '386cb0', 'f0027f'],
    7: ['7fc97f', 'beaed4', 'fdc086', 'ffff99', '386cb0', 'f0027f', 'bf5b17'],
    8: [
      '7fc97f',
      'beaed4',
      'fdc086',
      'ffff99',
      '386cb0',
      'f0027f',
      'bf5b17',
      '666666',
    ],
  }

  const sizes = Object.keys(palettes)
    .map(Number)
    .sort((a, b) => a - b)
  const maxSize = sizes[sizes.length - 1]

  if (count <= maxSize) {
    // Find the smallest stored palette that fits
    const bestSize = sizes.find((s) => s >= count) || maxSize
    const colors = palettes[bestSize]
    if (bestSize === count) {
      return colors.slice()
    }
    const result: string[] = []
    const step = (colors.length - 1) / (count - 1)
    for (let i = 0; i < count; i++) {
      result.push(colors[Math.round(i * step)])
    }
    return result
  }

  // For counts beyond 8, cycle through the largest palette
  const maxColors = palettes[maxSize]
  const result: string[] = []
  for (let i = 0; i < count; i++) {
    result.push(maxColors[i % maxColors.length])
  }
  return result
}
